import { createAsyncThunk } from '@reduxjs/toolkit'
import { SOMETHING_WHEN_WRONG } from 'constant/errorMessages'
import { StaffManagementRoleType } from 'features/SupplyOrder/@types/CheckQuantity'
import {
  getAccountInternalFeatureFlagByLocationApi,
  getInboundCheckQuantityApi,
  GetInboundCheckQuantityParamsType,
  postSubmitSoInboundItemsApi,
  PostSubmitSoInboundItemsPayloadType,
  putSubmitSoInboundCompletedApi,
  deleteInboundItemsApi,
  getStaffManagement,
  getSupplyOrderInboundHubProcessApi,
  postSupplyOrderInboundHubPutaway,
  PostSupplyOrderInboundHubPutawayPayloadType,
  PostCompleteSupplyOrderInboundProcessPayloadInterface,
  postCompleteSupplyOrderInboundProcessApi,
  patchSupplyOrderInboundItemDetailsApi,
  postSupplyOrderInboundItemDetailsDeleteApi,
  patchCompleteSupplyOrderInboundHubApi,
  type SupplyOrderInboundItemDetailsRackPayloadInterface,
  type SupplyOrderInboundItemDetailsExpiredDatePayloadInterface,
  type PostSupplyOrderInboundItemDetailsDeletePayloadInterface,
} from 'features/SupplyOrder/services/checkQuantity'
import { getHubFeatureFlag } from 'features/SupplyOrder/services/packingRecording'
import { callErrorMsg } from 'helpers/errorMsg'
import { postImage } from 'utils/apiList/images'
import { toastSuccess } from 'utils/toast'

export const SLICE_NAME_SO_CHECK_QUANTITY = 'soCheckQuantity'

export interface UpdateSupplyOrderInboundHubPutawayPayloadInterface {
  status: PostSupplyOrderInboundHubPutawayPayloadType['status']
  supplyOrderItemIds: PostSupplyOrderInboundHubPutawayPayloadType['supplyOrderItemIdList']
  staffIds: PostSupplyOrderInboundHubPutawayPayloadType['hubStaffIdList']
}

export const fetchAccountInternalFeatureFlagByLocation = createAsyncThunk(
  `${SLICE_NAME_SO_CHECK_QUANTITY}/fetchAccountInternalFeatureFlagByLocation`,
  async (locationId: number, { rejectWithValue }) => {
    const DEVICE_TYPE_ID = 1 /* 1 = web */
    try {
      const { data } = await getAccountInternalFeatureFlagByLocationApi({
        feature_flag_device: DEVICE_TYPE_ID,
        location_id: locationId,
      })

      return data.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)

export const fetchHubFeatureFlagByName = createAsyncThunk(
  `${SLICE_NAME_SO_CHECK_QUANTITY}/fetchHubFeatureFlagByName`,
  async (featureFlagName: string) => {
    try {
      const {
        data: { data },
      } = await getHubFeatureFlag({
        feature: featureFlagName,
      })

      return data
    } catch {
      return null
    }
  },
)

export const fetchInboundCheckQuantityByID = createAsyncThunk(
  `${SLICE_NAME_SO_CHECK_QUANTITY}/fetchInboundCheckQuantityByID`,
  async (
    { soID, params }: { soID: number; params: GetInboundCheckQuantityParamsType },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await getInboundCheckQuantityApi(soID, params)

      return data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)

export const fetchSupplyOrderInboundHubProcess = createAsyncThunk(
  `${SLICE_NAME_SO_CHECK_QUANTITY}/fetchSupplyOrderInboundHubProcess`,
  async ({ supplyOrderId }: { supplyOrderId: number }, { rejectWithValue }) => {
    try {
      const { data } = await getSupplyOrderInboundHubProcessApi(supplyOrderId)

      return data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)

export const submitSoInboundItems = createAsyncThunk(
  `${SLICE_NAME_SO_CHECK_QUANTITY}/submitSoInboundItems`,
  async (
    { soId, payload }: { soId: number; payload: PostSubmitSoInboundItemsPayloadType },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await postSubmitSoInboundItemsApi(soId, payload)

      return data
    } catch (err) {
      return rejectWithValue(err instanceof SyntaxError ? SOMETHING_WHEN_WRONG : err)
    }
  },
)

export const submitSoInboundCompleted = createAsyncThunk(
  `${SLICE_NAME_SO_CHECK_QUANTITY}/submitSoInboundCompleted`,
  async (soId: number, { rejectWithValue }) => {
    try {
      const { data } = await putSubmitSoInboundCompletedApi(soId)

      return data
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const deleteInboundItems = createAsyncThunk(
  `${SLICE_NAME_SO_CHECK_QUANTITY}/deleteInboundItems`,
  async (soItemIds: number[], { rejectWithValue }) => {
    try {
      const params = soItemIds.map((id) => `item_detail_ids=${id}`).join('&')
      const { data } = await deleteInboundItemsApi(`?${params}`)

      return data
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const fetchStaffManagement = createAsyncThunk(
  `${SLICE_NAME_SO_CHECK_QUANTITY}/fetchStaffManagement`,
  async (
    { locationId, role }: { locationId: number; role: StaffManagementRoleType },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await getStaffManagement(locationId, role)

      return data
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const updateSupplyOrderInboundHubPutaway = createAsyncThunk(
  `${SLICE_NAME_SO_CHECK_QUANTITY}/updateSupplyOrderInboundHubPutaway`,
  async (payload: UpdateSupplyOrderInboundHubPutawayPayloadInterface, { rejectWithValue }) => {
    try {
      const { data } = await postSupplyOrderInboundHubPutaway({
        status: payload.status,
        supplyOrderItemIdList: payload.supplyOrderItemIds,
        hubStaffIdList: payload.staffIds,
      })

      return data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)

export const completeSupplyOrderInboundProcess = createAsyncThunk(
  `${SLICE_NAME_SO_CHECK_QUANTITY}/completeSupplyOrderInboundProcess`,
  async (
    {
      supplyOrderId,
      payload,
    }: { supplyOrderId: number; payload: PostCompleteSupplyOrderInboundProcessPayloadInterface },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await postCompleteSupplyOrderInboundProcessApi(supplyOrderId, payload)
      toastSuccess(data.message)
      return data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)

export const uploadSupplyOrderImage = createAsyncThunk(
  `${SLICE_NAME_SO_CHECK_QUANTITY}/uploadSupplyOrderImage`,
  async (file: File, { getState }) => {
    const {
      auth: { userData },
    } = getState() as StoreStateType
    try {
      const { data } = await postImage('so-images', {
        created_by: userData.id,
        file_input: file,
      })

      return data
    } catch (err) {
      callErrorMsg(err)
      return {
        data: null,
      }
    }
  },
)

export const updateSupplyOrderInboundItemDetails = createAsyncThunk<
  { message: string },
  | SupplyOrderInboundItemDetailsExpiredDatePayloadInterface
  | SupplyOrderInboundItemDetailsRackPayloadInterface
>(
  `${SLICE_NAME_SO_CHECK_QUANTITY}/updateSupplyOrderInboundItemDetails`,
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await patchSupplyOrderInboundItemDetailsApi(payload)

      return data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)

export const deleteSupplyOrderInboundItemDetails = createAsyncThunk(
  `${SLICE_NAME_SO_CHECK_QUANTITY}/deleteSupplyOrderInboundItemDetails`,
  async (payload: PostSupplyOrderInboundItemDetailsDeletePayloadInterface, { rejectWithValue }) => {
    try {
      const { data } = await postSupplyOrderInboundItemDetailsDeleteApi(payload)

      return data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)

export const completeSupplyOrderInboundHub = createAsyncThunk(
  `${SLICE_NAME_SO_CHECK_QUANTITY}/completeSupplyOrderInboundHub`,
  async (supplyOrderId: number, { rejectWithValue }) => {
    try {
      const { data } = await patchCompleteSupplyOrderInboundHubApi(supplyOrderId)

      return data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)
